import type { BookingDetails } from '@/components/common/CalendarPopupButton';
import pcpSettings from '@/constants/pcp/constants';
import type { PCPGa4Event } from '@/constants/pcp/types';
import CalBooking from '@/pages/pcp/components/components/CalBooking';
import Dialog from '@/pages/pcp/components/components/Dialog';
import Step from '@/pages/pcp/components/components/Step';
import { PCPStepContext } from '@/pages/pcp/components/components/StepContextWrapper';
import useTracking from '@/pages/pcp/hooks/useTracking';
import { PCPDataContext } from '@/pages/pcp/PCPortal';
import { formatStringToSha256Hash } from '@/utils/formatStringToSha256Hash';
import { areAllFormsComplete, generateCalcomRescheduledByUrlParams } from '@/utils/pcp-helpers';
import { useContext, useRef } from 'react';

export default function DoctorConsult() {
  const { userData, fetchUserData } = useContext(PCPDataContext);
  const { allSteps, modalOpen, setModalOpen, url, isLink, ga4Events } = useContext(PCPStepContext);
  const allFormsComplete = areAllFormsComplete(allSteps);
  const hasTrackedBooking = useRef(false);

  const manageCalcomBookingUrl = generateCalcomRescheduledByUrlParams(url);
  const createDoctorBookingUrl = generateCalcomRescheduledByUrlParams(
    `${pcpSettings.calendarEmbedUrl}/team/alternaleaf-doctors/medical-cannabis-consultation`,
  );

  const { sendGa4Events } = useTracking();

  const handleButtonClick = (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    sendGa4Events(ga4Events?.buttonClickEvents);

    if (!isLink) {
      hasTrackedBooking.current = false;
      if (manageCalcomBookingUrl && mouseEvent.ctrlKey) {
        window.open(manageCalcomBookingUrl, '_blank');
        return;
      }
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    fetchUserData({
      updateLoadingState: !userData?.preDoctorConsultation.calcomBookingUrl ? true : false,
    });
  };

  const handleBookingSuccess = (bookingDetails?: BookingDetails) => {
    setModalOpen(false);
    fetchUserData({ updateLoadingState: false });

    if (!bookingDetails) {
      return;
    }

    // Prevent re-firing of tracking if tracking is already sent
    if (hasTrackedBooking.current) {
      return;
    }

    const bookingUid = bookingDetails.booking?.uid;
    const phone = bookingDetails.booking?.responses?.phoneNumber;
    const email = userData?.prefillFormData.email;

    const bookingId = formatStringToSha256Hash(bookingUid);
    const shaUserEmail = formatStringToSha256Hash(email);
    const shaUserPhone = formatStringToSha256Hash(phone);

    if (!bookingId || !shaUserEmail || !shaUserPhone) {
      return;
    }

    const bookingSuccessTrackingPayload: PCPGa4Event[] = [
      {
        event: 'ga4_4__submit_booking',
        event_category: 'cal.com',
        event_label: 'Doctor Consultation',
        booking_id: bookingId,
        promocode: '',
        sha_city: '',
        sha_country: '',
        sha_email: shaUserEmail,
        sha_fb_phone_number: shaUserPhone,
        sha_gads_phone_number: shaUserPhone,
      },
    ];

    sendGa4Events(bookingSuccessTrackingPayload);

    hasTrackedBooking.current = true;
  };

  const isPatientIntiatedDoc =
    userData?.preDoctorConsultation.calcomBookingUrl === null &&
    !userData?.preDoctorConsultation.completed &&
    !userData?.preDoctorConsultation.consultationBooked &&
    userData?.preNurseConsultation.completed;

  return (
    <>
      <Step onButtonClick={handleButtonClick} divider buttonVariant={allFormsComplete ? 'contained' : 'outlined'} />

      <Dialog open={modalOpen} onCloseButtonClick={handleModalClose}>
        <CalBooking
          url={isPatientIntiatedDoc ? createDoctorBookingUrl : manageCalcomBookingUrl}
          email={userData?.prefillFormData?.email}
          handleBookingSuccess={handleBookingSuccess}
          handleRescheduleSuccess={handleBookingSuccess}
          handleCancelSuccess={handleModalClose}
        />
      </Dialog>
    </>
  );
}
